import React, { useEffect, useRef, useState } from 'react';
import { conversationService, templateService } from '../services/api';

const ConversationDetail = ({
  conversation,
  isLoading,
  onSendToClinic,
  onSendMessage,
  showRefreshButton,
  onRefresh,
  lastRefreshTime,
  onLoadMoreMessages,
  isLoadingMore,
  hasMoreMessages
}) => {
  // Create a ref for the messages container
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [message, setMessage] = useState('');
  // Track whether we should scroll to bottom
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  // Track last messages length to determine if new messages arrived
  const lastMessagesLengthRef = useRef(0);
  // Track if messages were loaded from the "Load more" button
  const [loadedViaButton, setLoadedViaButton] = useState(false);

  // State for template dialog
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  // Function to instantly teleport to bottom (no smooth scrolling)
  const teleportToBottom = (forceScroll = false) => {
    if (messagesContainerRef.current && (shouldScrollToBottom || forceScroll) && !loadedViaButton) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // Alternative scroll method using messagesEndRef
  const scrollToBottom = (forceScroll = false) => {
    if (messagesEndRef.current && (shouldScrollToBottom || forceScroll) && !loadedViaButton) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
  };

  // Initial load scroll to bottom
  useEffect(() => {
    // Scroll when component first mounts
    teleportToBottom(true);
    scrollToBottom(true);
  }, []);

  // Added: Teleport to bottom when messages initially load
  useEffect(() => {
    // This ensures we scroll to bottom when messages first load
    // but not when messages are loaded via the "Load more" button
    if (conversation?.messages && conversation.messages.length > 0 && !loadedViaButton) {
      setTimeout(() => {
        teleportToBottom(true); // Force scroll regardless of shouldScrollToBottom state
        scrollToBottom(true); // Also use the scrollIntoView method
      }, 100); // Small delay to ensure DOM is fully rendered
    }
  }, [conversation?.messages?.length, isLoadingMore, loadedViaButton]);

  // Function to handle sending a message
  const sendMessage = () => {
    if (message.trim() && conversation?.conversation_id && onSendMessage) {
      onSendMessage(conversation.conversation_id, message.trim());
      setMessage(''); // Clear the input field after sending
    }
  };

  // Handle keypress event for the input field
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent default Enter behavior
      sendMessage();
    }
  };

  // Function to open template dialog and fetch templates
  const openTemplateDialog = async () => {
    setIsTemplateDialogOpen(true);
    setIsLoadingTemplates(true);

    try {
      const response = await templateService.getTemplates();
      setTemplates(response.templates);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  // Function to send selected template
  const sendTemplate = async (templateId) => {
    if (!conversation?.conversation_id) return;

    try {
      await conversationService.sendTemplate(conversation.conversation_id, templateId);
      // After sending, close the dialog and refresh messages if needed
      setIsTemplateDialogOpen(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error sending template:', error);
    }
  };

  // Teleport to bottom when component mounts or conversation changes
  useEffect(() => {
    // Only scroll to bottom for new conversation if not loaded via button
    if (!loadedViaButton) {
      setShouldScrollToBottom(true);
      teleportToBottom();
    }

    // Reset last messages length
    if (conversation?.messages) {
      lastMessagesLengthRef.current = conversation.messages.length;
    }
  }, [conversation?.conversation_id, loadedViaButton]);

  // Teleport to bottom only when new messages arrive
  useEffect(() => {
    if (conversation?.messages) {
      // Only scroll if new messages have been added AND not loaded via button
      if (conversation.messages.length > lastMessagesLengthRef.current && !loadedViaButton) {
        teleportToBottom();
      }

      // Update last messages length
      lastMessagesLengthRef.current = conversation.messages.length;
    }
  }, [conversation?.messages, loadedViaButton]);

  // Function to format message content by replacing newlines with <br/> tags
  const formatMessageContent = (content) => {
    if (!content) return '';

    // Replace all occurrences of \n or \r\n with <br/>
    const formattedContent = content
      .replace(/\r\n/g, '<br/>')
      .replace(/\n/g, '<br/>')
      .replace(/\r/g, '<br/>');

    return (
      <span dangerouslySetInnerHTML={{ __html: formattedContent }} />
    );
  };

  // Function to render media preview based on media_url and media_type
  const renderMediaPreview = (message) => {
    if (!message.media_url) return null;

    const mediaType = message.message_type || '';
    const mediaUrl = message.media_url;

    // Always try to display images directly if URL looks like an image or has image media type
    if (mediaType === 'image') {
      return (
        <div className="mb-2">
          <img
            src={mediaUrl}
            alt="Media attachment"
            className="rounded h-[200px] w-[200px] cursor-pointer"
            onClick={() => window.open(mediaUrl, '_blank')}
            onError={(e) => {
              // If image fails to load, replace with download link
              e.target.style.display = 'none';
              e.target.parentNode.innerHTML = `
                <a href="${mediaUrl}" target="_blank" rel="noopener noreferrer" class="flex items-center text-blue-600 hover:underline">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download attachment
                </a>
              `;
            }}
          />
        </div>
      );
    } else if (mediaType === 'audio') {
      return (
        <div className="mb-2">
          <audio controls className="w-full">
            <source src={mediaUrl} type={mediaType} />
            Your browser does not support the audio element.
          </audio>
        </div>
      );
    } else if (mediaType === 'video') {
      return (
        <div className="mb-2">
          <video controls className="w-full max-h-60 rounded">
            <source src={mediaUrl} type={mediaType} />
            Your browser does not support the video element.
          </video>
        </div>
      );
    } else if (mediaUrl) {
      // For any other media type or unknown types, show a download link
      return (
        <div className="mb-2">
          <a
            href={mediaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center text-blue-600 hover:underline"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download attachment
          </a>
        </div>
      );
    }

    return null;
  };

  if (!conversation) {
    return (
      <div className="h-[70vh] w-full flex items-center justify-center">
        <p className="text-gray-500">Select a conversation to view details</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col h-[70vh] w-full bg-white rounded-lg overflow-hidden shadow-md">
        {/* WhatsApp-like header */}
        <div className="bg-[#075e54] text-white p-3 flex items-center">
          <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center mr-3">
            {conversation.profile_name ? conversation.profile_name.charAt(0).toUpperCase() : '#'}
          </div>
          <div>
            <h3 className="font-semibold">
              {conversation.profile_name || conversation.conversation_id}
            </h3>
            <p className="text-xs opacity-80">
              {conversation.channel || 'Unknown channel'}
            </p>
          </div>
        </div>

        {/* Loading indicator */}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-[#075e54] border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading messages...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!conversation.messages || conversation.messages.length === 0) {
    return (
      <div className="flex flex-col h-[70vh] w-full bg-white rounded-lg overflow-hidden shadow-md">
        {/* WhatsApp-like header */}
        <div className="bg-[#075e54] text-white p-3 flex items-center">
          <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center mr-3">
            {conversation.profile_name ? conversation.profile_name.charAt(0).toUpperCase() : '#'}
          </div>
          <div>
            <h3 className="font-semibold">
              {conversation.profile_name || conversation.conversation_id}
            </h3>
            <p className="text-xs opacity-80">
              {conversation.channel || 'Unknown channel'}
            </p>
          </div>
        </div>

        {/* No messages indicator */}
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-500">No messages in this conversation</p>
        </div>
      </div>
    );
  }

  // Determine message direction based on from_number and to_number
  const getMessageDirection = (message) => {
    if (!message) return 'outbound';

    // If the message has a direction property, use it
    if (message.direction) return message.direction;

    // Otherwise, determine direction based on role or from_number
    if (message.role === 'assistant' || message.role === 'system' || message.role === 'mctb') {
      return 'outbound';
    } else if (message.role === 'user') {
      return 'inbound';
    }

    // Fallback to checking from_number (this depends on your specific data structure)
    // You might need to adjust this logic based on your actual data
    return message.from_number === conversation.to_number ? 'outbound' : 'inbound';
  };

  return (
    <div className="flex flex-col h-[70vh] w-full bg-white rounded-lg overflow-hidden shadow-md">
     {/* WhatsApp-like header with test case controls */}
      <div className="bg-[#075e54] text-white p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center mr-3">
              {conversation.profile_name ? conversation.profile_name.charAt(0).toUpperCase() : '#'}
            </div>
            <div>
              <h3 className="font-semibold">
                {conversation.profile_name || conversation.conversation_id}
              </h3>
              <p className="text-xs opacity-80">
                {conversation.channel || 'Unknown channel'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => window.exportTestCases && window.exportTestCases()}
              className="px-3 py-1 rounded text-sm font-medium flex items-center bg-indigo-600 hover:bg-indigo-700 text-white"
              title="Export Test Cases"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Export test case</span>
            </button>
          </div>
        </div>
      </div>

      {/* Chat area with white background and scroll handler */}
      <div
        ref={messagesContainerRef}
        className="flex-1 p-4 overflow-y-auto space-y-3 bg-white"
        onScroll={(e) => {
          // Determine if user has scrolled up
          const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
          const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 10;
          setShouldScrollToBottom(isAtBottom);
        }}
      >
        {/* Load more messages button - only show if we have messages and hasMoreMessages is true */}
        {conversation?.messages?.length > 0 && hasMoreMessages && onLoadMoreMessages && (
          <div className="flex justify-center mb-4">
            <button
              onClick={() => {
                // Set the flag to indicate messages are being loaded via button
                setLoadedViaButton(true);
                onLoadMoreMessages(conversation.conversation_id);
              }}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded flex items-center"
              disabled={isLoadingMore}
            >
              {isLoadingMore ? (
                <>
                  <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                  Loading...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                  Load more messages
                </>
              )}
            </button>
          </div>
        )}

        {conversation?.messages?.map((message) => {
          // Special case for appointment messages
          if (message.is_appointment) {
            return (
              <div key={message.id} className="flex justify-center">
                <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg max-w-[85%] shadow">
                  <div className="flex items-center">
                    <span className="mr-2">📅</span>
                    <p className="text-sm text-blue-800 font-medium">{formatMessageContent(message.message_content)}</p>
                  </div>
                  <div className="mt-1 text-xs text-blue-600 text-right">
                    <span className="bg-blue-100 px-2 py-1 rounded capitalize">{message.appointment_status}</span>
                    <span className="ml-2">{new Date(message.timestamp).toLocaleString()}</span>
                    {message.appointment?.has_notified &&
                      <span className="ml-2 bg-green-100 text-green-800 px-2 py-1 rounded">
                        Notified ✓
                      </span>
                    }
                  </div>
                </div>
              </div>
            );
          }

          // Special case for event messages (notify_clinic_by_human)
          if (message.is_event) {
            return (
              <div key={message.id} className="flex justify-center">
                <div className="bg-green-50 border border-green-200 p-3 rounded-lg max-w-[85%] shadow">
                  <div className="flex items-center">
                    <span className="mr-2">📢</span>
                    <p className="text-sm text-green-800 font-medium">{formatMessageContent(message.message_content)}</p>
                  </div>
                  <div className="mt-1 text-xs text-green-600 text-right">
                    <span className="bg-green-100 px-2 py-1 rounded">
                      {
                        message.event_type === "notify_clinic_by_human" ? "Notified Clinic by Human" : "Notified Clinic"
                      }
                    </span>
                    <span className="ml-2">{new Date(message.timestamp).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            );
          }

          // Regular message handling
          const messageDirection = getMessageDirection(message);

          return (
            <div
              key={message.id}
              className={`flex ${messageDirection === 'outbound' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`p-3 rounded-lg max-w-[85%] shadow ${
                  messageDirection === 'outbound'
                    ? 'bg-[#dcf8c6]'
                    : 'bg-white border border-gray-200'
                }`}
              >
                {renderMediaPreview(message)}
                <p className="text-sm">{formatMessageContent(message.message_content)}</p>
                <div className="mt-1 text-xs text-gray-500 text-right">
                  {new Date(message.timestamp).toLocaleString()}
                </div>
              </div>
            </div>
          );
        })}

        {/* Refresh button after messages - without changing scroll position */}
        {showRefreshButton && (
          <div className="bg-blue-50 p-2 mt-4 rounded flex justify-center">
            <button
              onClick={onRefresh}
              className="text-blue-600 text-sm font-medium hover:text-blue-800 flex items-center"
              disabled={isLoading}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              Refresh - last update: {lastRefreshTime}
            </button>
          </div>
        )}
      </div>

      {/* Templates dialog */}
      {isTemplateDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Available Templates</h3>
              <button
                onClick={() => setIsTemplateDialogOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {isLoadingTemplates ? (
              <div className="py-8 flex justify-center">
                <div className="w-8 h-8 border-4 border-[#128c7e] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                {templates.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No templates available</p>
                ) : (
                  <ul className="space-y-2">
                    {templates.map((template) => (
                      <li key={template.id} className="border rounded-lg p-3 hover:bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium text-gray-800">{template.id}</h4>
                            <p className="text-sm text-gray-500 mt-1">{template.content}</p>
                          </div>
                          <button
                            onClick={() => sendTemplate(template.id)}
                            className="ml-2 bg-[#128c7e] text-white px-3 py-1 rounded text-sm hover:bg-[#075e54]"
                          >
                            Select
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* WhatsApp-like input area with Notify Clinic button and Send Template button */}
      <div className="bg-[#f0f0f0] p-2 flex items-center">
        <button
          onClick={() => onSendToClinic(conversation.conversation_id)}
          className="mr-2 bg-[#128c7e] bg-opacity-80 text-white px-2 py-1 rounded text-sm hover:bg-opacity-100"
        >
          Notify Clinic
        </button>
        <button
          onClick={openTemplateDialog}
          className="mr-2 bg-[#0d6efd] bg-opacity-80 text-white px-2 py-1 rounded text-sm hover:bg-opacity-100"
        >
          Send Template
        </button>
        <div className="flex-1 bg-white rounded-full px-4 py-2 flex items-center">
          <input
            type="text"
            className="flex-1 outline-none"
            placeholder="Type a message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <button
            onClick={sendMessage}
            className="ml-2 text-[#128c7e] hover:text-[#075e54]"
            disabled={!message.trim()}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConversationDetail;
